import React from 'react';
import type { CommunicationTheme, InvestorTheme, WealthsweetTheme } from '@seccl/client';
import { createRootCSSVariables } from '@seccl/ui';
import NextHead from 'next/head';

import { useGetNodeTheme } from '../../api/hooks/firm/getNodeTheme.hook';
import { useBranding } from '../../context/branding.context';
import { getSecclDefaultBranding } from '../../helpers/branding/defaultBranding';
import type { IFirmBranding } from '../../helpers/branding/types';

interface IHeadProps {
  nodeId?: string;
  branding?: IFirmBranding;
}

/**
 * A wrapper component for next/head. This component manages the css theme variables that are injected into the <head>.
 */
export const Head: React.FC<IHeadProps> = ({ nodeId, branding }) => {
  const defaultBranding = getSecclDefaultBranding();
  const { data } = useGetNodeTheme(
    { nodeId },
    {
      data: {
        professional: {
          backgroundColour: {
            sourceNodeId: '0',
            value: branding?.backgroundColor ?? defaultBranding.backgroundColor,
          },
          backgroundColourContrast: {
            sourceNodeId: '0',
            value: branding?.backgroundColorContrast ?? defaultBranding.backgroundColorContrast ?? '',
          },
          ctaColour: {
            sourceNodeId: '0',
            value: branding?.ctaColor ?? defaultBranding.ctaColor,
          },
          ctaColourContrast: {
            sourceNodeId: '0',
            value: branding?.ctaColorContrast ?? defaultBranding.ctaColorContrast ?? '',
          },
          primaryColour: {
            sourceNodeId: '0',
            value: branding?.brandColor ?? defaultBranding.brandColor,
          },
        },
        investor: {} as InvestorTheme,
        communication: {} as CommunicationTheme,
        wealthsweet: {} as WealthsweetTheme,
      },
    }
  );
  const { getLogoUrl } = useBranding();
  const favicon = getLogoUrl('favicon');

  const cssVars = React.useMemo(() => {
    const branding = {
      'brand-primary': data?.data.professional.backgroundColour.value,
      'brand-secondary': data?.data.professional.primaryColour.value,
      accent: data?.data.professional.ctaColour.value,
      'brand-primary-contrast': data?.data.professional.backgroundColourContrast.value,
      'accent-contrast': data?.data.professional.ctaColourContrast.value,
    };
    return createRootCSSVariables(branding);
  }, [
    data?.data.professional.backgroundColour.value,
    data?.data.professional.backgroundColourContrast.value,
    data?.data.professional.ctaColour.value,
    data?.data.professional.ctaColourContrast.value,
    data?.data.professional.primaryColour.value,
  ]);

  return (
    <NextHead>
      <style>{cssVars}</style>
      {favicon && <link rel="shortcut icon" href={favicon} />}
    </NextHead>
  );
};
